import React, { createContext, useContext, useState, useEffect } from 'react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'

type ThemeMode = 'light' | 'dark'

interface ThemeContextValue {
  mode: ThemeMode
  toggleMode: () => void
  theme: ReturnType<typeof createTheme>
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// BlackBook brand colors
const brandColors = {
  primary: {
    main: '#1A365D',
    light: '#2D5A87',
    dark: '#0F2A44',
  },
  secondary: {
    main: '#00C9A7', // BlackBook teal for UI highlights
    light: '#9AE9D9',
    dark: '#00A085',
  },
  tertiary: {
    main: '#FE5300', // Orange accent
    light: '#FF7A33',
    dark: '#E04A00',
  },
}

const createAppTheme = (mode: ThemeMode) => createTheme({
  palette: {
    mode,
    primary: brandColors.primary,
    secondary: brandColors.secondary,
    tertiary: brandColors.tertiary,
    ...(mode === 'light' ? {
      background: {
        default: '#FAFAFA',
        paper: '#FFFFFF',
      },
    } : {
      background: {
        default: '#121212',
        paper: '#1E1E1E',
      },
    }),
  },
  typography: {
    fontFamily: '"Roboto", "Europa", "Microsoft Sans Serif", Arial, Helvetica, sans-serif',
    h1: { fontFamily: '"Europa Bold", "Roboto", Arial, sans-serif' },
    h2: { fontFamily: '"Europa Bold", "Roboto", Arial, sans-serif' },
    h3: { fontFamily: '"Europa Bold", "Roboto", Arial, sans-serif' },
    h4: { fontFamily: '"Europa Bold", "Roboto", Arial, sans-serif' },
    h5: { fontFamily: '"Europa Bold", "Roboto", Arial, sans-serif' },
    h6: { fontFamily: '"Europa Bold", "Roboto", Arial, sans-serif' },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1005,
      xl: 1920,
    },
  },
})

interface CustomThemeProviderProps {
  children: React.ReactNode
}

export function CustomThemeProvider({ children }: CustomThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>('light')

  // Load theme preference from localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem('themeMode') as ThemeMode
    if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
      setMode(savedMode)
    }
  }, [])

  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light'
    setMode(newMode)
    localStorage.setItem('themeMode', newMode)
  }

  const theme = createAppTheme(mode)

  const contextValue: ThemeContextValue = {
    mode,
    toggleMode,
    theme,
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  )
}